import { ArrowLeft, Eye, History, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { HistoryDrawer } from "@/components/HistoryDrawer";
import { Button } from "@/components/ui/button";
import { ActionPlansPage } from "@/features/action-plans";
import { QuestionnairePage } from "@/features/questionnaire";
import { useMemberHistory } from "@/features/questionnaire/api";
import { setOnBehalfOf,
  setOnBehalfReadOnly } from "@/lib/api/on-behalf";
import { ReadOnlyProvider } from "@/lib/read-only";
import { cn } from "@/lib/utils/cn";

import { usePartnerConsentQuery } from "../api";
import type { MemberTab } from "./MemberTable";

/**
 * Partner acting on a consented member's data (area I). Sets the on-behalf
 * target so the *existing* questionnaire + action-plan pages operate on the
 * member (their API calls carry `?on_behalf_of` and their caches are keyed by
 * member — see @/lib/api/on-behalf). Editing is gated server-side by the
 * member's edit consent; without it, writes fail and the autosave shows an error.
 */
export function MemberDataView({
  profileId,
  memberName,
  initialTab = "diagnostic",
  onBack,
}: {
  profileId: string;
  memberName: string;
  initialTab?: MemberTab;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<MemberTab>(initialTab);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Only fetched once opened; the on-behalf context is already set below.
  const history = useMemberHistory(historyOpen);
  // Read-only unless the member granted edit consent (G-11). Defaults to
  // read-only while the consent query loads — never assume edit rights.
  const consent = usePartnerConsentQuery(profileId);
  const readOnly = consent.data?.consent_edit_at == null;

  // Set synchronously so the child pages' query keys (computed during their
  // render) already target the member; clear on unmount.
  setOnBehalfOf(profileId);
  setOnBehalfReadOnly(readOnly);
  useEffect(() => {
    setOnBehalfOf(profileId);
    setOnBehalfReadOnly(readOnly);
    return () => {
      setOnBehalfOf(null);
      setOnBehalfReadOnly(false);
    };
  }, [profileId, readOnly]);

  const tabs: MemberTab[] = ["diagnostic", "plan"];

  return (
    <div className="space-y-6">
      <div className="rounded-md border border-primary/30 bg-primary/5 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <p className="flex items-center gap-2 text-sm font-medium">
              <Eye className="size-4 text-primary" aria-hidden />
              {t("partners.memberData.banner", { name: memberName })}
            </p>
            {readOnly && (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Lock className="size-3.5" aria-hidden />
                {t("partners.memberData.readOnly")}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Everything that happened to this member's data, in one list —
                the per-page drawers answer it a page at a time. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHistoryOpen(true)}
            >
              <History className="mr-1.5 size-4" aria-hidden />
              {t("partners.memberData.history")}
            </Button>
            <Button variant="outline" size="sm" onClick={onBack}>
              <ArrowLeft className="mr-1.5 size-4" aria-hidden />
              {t("partners.memberData.back")}
            </Button>
          </div>
        </div>
      </div>

      <div
        role="tablist"
        aria-label={t("partners.memberData.viewLabel")}
        className="inline-flex rounded-md border border-input p-0.5"
      >
        {tabs.map((tb) => (
          <button
            key={tb}
            type="button"
            role="tab"
            aria-selected={tab === tb}
            onClick={() => setTab(tb)}
            className={cn(
              "min-h-touch rounded px-3 py-1.5 text-sm font-medium transition-colors",
              tab === tb
                ? "bg-primary text-primary-foreground"
                : "hover:bg-accent hover:text-accent-foreground",
            )}
          >
            {t(`partners.memberData.tabs.${tb}`)}
          </button>
        ))}
      </div>

      {historyOpen && (
        <HistoryDrawer
          title={memberName}
          onClose={() => setHistoryOpen(false)}
          query={history}
        />
      )}

      <ReadOnlyProvider value={readOnly}>
        {tab === "diagnostic" ? <QuestionnairePage /> : <ActionPlansPage />}
      </ReadOnlyProvider>
    </div>
  );
}
